/**
 * useFieldValues
 *
 * Pure computation hook for resolving template field values.
 * No internal state — the parent owns overrides and dirty tracking.
 *
 * Resolution precedence: Override -> Context (via context_key) -> Default -> Empty
 */
import { useMemo } from 'react';

import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';

import type {
  FieldValueInfo,
  MatterContext,
  MissingFieldInfo,
} from '../utils/fieldValidation';
import {
  getFieldDisplayName,
  getFieldValueInfo,
  getOverrideKey,
  isFieldRequired,
  isFieldValueMissing,
  validateTemplateFields,
} from '../utils/fieldValidation';

// ============================================================================
// Types
// ============================================================================

export interface ResolvedField {
  field: ReadTemplateField;
  overrideKey: string;
  displayName: string;
  valueInfo: FieldValueInfo;
  required: boolean;
  isMissing: boolean;
}

export interface UseFieldValuesReturn {
  /** Resolved field data for rendering */
  fields: ResolvedField[];
  /** Whether all required fields have values */
  isValid: boolean;
  /** List of fields that are required but missing values */
  missingFields: MissingFieldInfo[];
}

// ============================================================================
// Hook
// ============================================================================

export function useFieldValues(
  templateFields: ReadTemplateField[],
  matterContext: MatterContext,
  overrides: Record<string, string>,
): UseFieldValuesReturn {
  const contextKey = JSON.stringify(matterContext);

  const fields = useMemo<ResolvedField[]>(() => {
    return templateFields.map((field) => {
      const overrideKey = getOverrideKey(field);
      const valueInfo = getFieldValueInfo(field, matterContext, overrides);
      const required = isFieldRequired(field);
      const isMissing = required && isFieldValueMissing(valueInfo.value);

      return {
        field,
        overrideKey,
        displayName: getFieldDisplayName(field),
        valueInfo,
        required,
        isMissing,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateFields, contextKey, overrides]);

  const validation = useMemo(
    () => validateTemplateFields(templateFields, matterContext, overrides),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateFields, contextKey, overrides],
  );

  return {
    fields,
    isValid: validation.isValid,
    missingFields: validation.missingFields,
  };
}
